// src/components/Navbar.js - VERSION FINALE SWIPE HAUT + CLICK OUTSIDE + LEADER LINK

import React, { useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ThemeToggleButton from './ThemeToggleButton';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBell, faSignOutAlt, faHome, faUser, faTrophy, faChartLine } from '@fortawesome/free-solid-svg-icons'; // ✅ AJOUT faChartLine
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import { useSocket } from '../SocketContext';
import './Navbar.css';

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const [scrolled, setScrolled] = React.useState(false);

  // ✅ Récupération de l'utilisateur et de son rôle depuis localStorage
  const user = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, []);

  const isLeader = user?.role === 'leader';

  // Notifications
  const { notifications } = useSocket();
  const unreadCount = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return 0;
    return notifications.reduce((count, n) => (n.read === false ? count + 1 : count), 0);
  }, [notifications]);

  /* ---------- SWIPE VERS LE HAUT + CLICK-OUTSIDE ---------- */
  useEffect(() => {
    let touchStartY = 0;

    const onTouchStart = e => (touchStartY = e.touches[0].clientY);
    const onTouchEnd = e => {
      const diff = touchStartY - e.changedTouches[0].clientY; // positif = vers le haut
      if (diff > 50) {
        const collapse = document.getElementById('navbarNav');
        if (collapse && collapse.classList.contains('show')) {
          window.bootstrap?.Collapse.getInstance(collapse)?.hide();
        }
      }
    };

    const onClickOutside = e => {
      const nav = document.querySelector('.navbar');
      if (nav && !nav.contains(e.target)) {
        const collapse = document.getElementById('navbarNav');
        if (collapse && collapse.classList.contains('show')) {
          window.bootstrap?.Collapse.getInstance(collapse)?.hide();
        }
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('mousedown', onClickOutside);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  /* ---------- EFFET SCROLL ---------- */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ---------- CLASSES DYNAMIQUES ---------- */
  const navbarClasses = useMemo(() => {
    const base = ['navbar', 'navbar-expand-lg', 'fixed-top', 'transition-all', scrolled ? 'navbar-scrolled' : '', `theme-${theme}`];
    base.push(theme === 'dark' ? 'navbar-dark bg-dark' : 'navbar-light bg-light');
    return base.filter(Boolean).join(' ');
  }, [theme, scrolled]);

  /* ---------- AUTH ---------- */
  const isAuthenticated = useMemo(() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  }, []);

  /* ---------- LOGOUT ---------- */
  const handleLogout = useCallback(() => {
    if (window.confirm(t('nav_logout_confirm') || 'Êtes-vous sûr de vouloir vous déconnecter ?')) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userData');
        navigate('/', { replace: true });
      } catch (e) { console.error(e); }
    }
  }, [navigate, t]);

  /* ---------- LIEN ACTIF ---------- */
  const isActive = useCallback(path => (location.pathname === path ? 'active' : ''), [location.pathname]);

  /* ---------- BURGER BUTTON ---------- */
  const renderMobileMenuButton = () => (
    <button
      className="navbar-toggler"
      type="button"
      data-bs-toggle="collapse"
      data-bs-target="#navbarNav"
      aria-controls="navbarNav"
      aria-expanded="false"
      aria-label={t('nav_toggle_label') || 'Menu'}
    >
      <span className="navbar-toggler-icon" />
    </button>
  );

  /* ---------- LIENS AUTH ---------- */
  const renderAuthenticatedLinks = () => (
    <>
      <li className="nav-item"><Link className={`nav-link d-flex align-items-center ${isActive('/dashboard')}`} to="/dashboard"><FontAwesomeIcon icon={faHome} className="me-2" /><span>{t('nav_dashboard')}</span></Link></li>
      <li className="nav-item"><Link className={`nav-link d-flex align-items-center ${isActive('/profile')}`} to="/profile"><FontAwesomeIcon icon={faUser} className="me-2" /><span>{t('nav_profile')}</span></Link></li>
      <li className="nav-item"><Link className={`nav-link d-flex align-items-center ${isActive('/leaderboard')}`} to="/leaderboard"><FontAwesomeIcon icon={faTrophy} className="me-2" /><span>{t('nav_leaderboard')}</span></Link></li>
      <li className="nav-item"><Link className={`nav-link d-flex align-items-center ${isActive('/gemini-chat')}`} to="/gemini-chat"><FontAwesomeIcon icon={faRobot} className="me-2" /><span>{t('nav_ai_agent')}</span></Link></li>
      
      {/* ✅ LIEN LEADER CONDITIONNEL */}
      {isLeader && (
        <li className="nav-item"><Link className={`nav-link d-flex align-items-center ${isActive('/mes-commissions')}`} to="/mes-commissions"><FontAwesomeIcon icon={faChartLine} className="me-2" /><span>Mes commissions</span></Link></li>
      )}
      
      <li className="nav-item position-relative"><Link className="nav-link position-relative" to="/notifications"><FontAwesomeIcon icon={faBell} className="fs-5" />{unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}</Link></li>
      <li className="nav-item"><button onClick={handleLogout} className="btn btn-outline-danger d-flex align-items-center logout-btn"><FontAwesomeIcon icon={faSignOutAlt} className="me-2" /><span>{t('nav_logout')}</span></button></li>
    </>
  );

  /* ---------- LIENS NON-AUTH ---------- */
  const renderUnauthenticatedLinks = () => (
    <>
      <li className="nav-item"><Link className={`nav-link ${isActive('/register')}`} to="/register">{t('nav_register')}</Link></li>
      <li className="nav-item"><Link className="btn btn-primary login-btn" to="/">{t('nav_login')}</Link></li>
    </>
  );

  /* ---------- RENDU ---------- */
  return (
    <>
      <nav className={navbarClasses} role="navigation" aria-label="Navigation principale">
        <div className="container-fluid container-lg">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to={isAuthenticated ? '/dashboard' : '/'} aria-label="Blackroq - Retour à l'accueil">
            <span className="brand-text">BLACKROQ</span>
          </Link>

          {renderMobileMenuButton()}

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              <li className="nav-item me-3 mb-2 mb-lg-0"><div className="d-flex align-items-center"><LanguageSwitcher compact /></div></li>
              <li className="nav-item me-3 mb-2 mb-lg-0"><div className="d-flex align-items-center"><ThemeToggleButton /></div></li>
              {isAuthenticated ? renderAuthenticatedLinks() : renderUnauthenticatedLinks()}
            </ul>
          </div>
        </div>
      </nav>
      <div className="navbar-spacer" />
    </>
  );
};

export default Navbar;